/**
 * Chart Patterns, Elliott Waves & Cycles primitives.
 *
 * All pattern/wave tools are N-point labeled paths.
 * Cycles are center+radius circle tools.
 */

const HANDLE_RADIUS = 5;
const HANDLE_COLOR = '#6366f1';
const HANDLE_HIT = 8;
const HIT_TOLERANCE = 7;

function drawHandle(ctx, x, y, hpr, vpr) {
  ctx.beginPath();
  ctx.arc(x * hpr, y * vpr, HANDLE_RADIUS * hpr, 0, Math.PI * 2);
  ctx.fillStyle = HANDLE_COLOR; ctx.fill();
  ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5 * hpr; ctx.stroke();
}

function hexToRgba(hex, a) {
  const c = hex.replace('#', '');
  return `rgba(${parseInt(c.slice(0,2),16)},${parseInt(c.slice(2,4),16)},${parseInt(c.slice(4,6),16)},${a})`;
}

class Base {
  constructor() { this._chart = null; this._series = null; this._requestUpdate = null; this.isHovered = false; this.isSelected = false; this.lineWidth = undefined; this.lineStyle = undefined; }
  attached({ requestUpdate, chart, series }) { this._requestUpdate = requestUpdate; this._chart = chart; this._series = series; }
  detached() { this._requestUpdate = null; this._chart = null; this._series = null; }
  requestUpdate() { this._requestUpdate?.(); }
  hitTest() { return Infinity; }
  isNear(x, y) { return this.hitTest(x, y) <= HIT_TOLERANCE; }
  hitTestHandle() { return -1; }
  moveBy() {}
  moveHandle() {}
  _ts() { return this._chart?.timeScale(); }
  _toXY(logical, price) {
    return { x: this._ts()?.logicalToCoordinate(logical), y: this._series?.priceToCoordinate(price) };
  }
}

// ═══════════════════════════════════════════════════════════════════
// LABELED PATH — Generic N-point path with per-point labels
// Used for: Chart Patterns (XABCD, H&S, etc.) and Elliott Waves
// ═══════════════════════════════════════════════════════════════════

class LabeledPathPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const pts = s._screenPts;
      if (!pts || pts.length < 2) return;
      ctx.save();

      // Filled area between path and baseline (optional)
      if (s.showFill && pts.length >= 3) {
        ctx.fillStyle = hexToRgba(s.color, 0.05);
        ctx.beginPath();
        ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
        for (let i = 1; i < pts.length; i++) {
          if (pts[i].x == null) continue;
          ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
        }
        ctx.closePath();
        ctx.fill();
      }

      // Hover glow
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.25);
        ctx.lineWidth = 6 * hpr;
        ctx.beginPath();
        ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
        for (let i = 1; i < pts.length; i++) {
          if (pts[i].x == null) continue;
          ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
        }
        ctx.stroke();
      }

      // Main path
      ctx.strokeStyle = s.color;
      ctx.lineWidth = (s.lineWidth || 2) * hpr;
      ctx.setLineDash(s.dashed ? [5 * hpr, 3 * hpr] : []);
      ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(pts[0].x * hpr, pts[0].y * vpr);
      for (let i = 1; i < pts.length; i++) {
        if (pts[i].x == null) continue;
        ctx.lineTo(pts[i].x * hpr, pts[i].y * vpr);
      }
      ctx.stroke();
      ctx.setLineDash([]);

      // Point labels
      const labels = s.labels || [];
      const fontSize = (s.labelSize || 11) * vpr;
      ctx.font = `bold ${fontSize}px -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
      ctx.textAlign = 'center';

      for (let i = 0; i < pts.length; i++) {
        const pt = pts[i];
        if (pt.x == null) continue;
        const px = pt.x * hpr, py = pt.y * vpr;
        const label = labels[i] || '';

        if (label) {
          // Determine if label goes above or below the point
          const prev = i > 0 ? pts[i-1] : null;
          const next = i < pts.length - 1 ? pts[i+1] : null;
          const isLocalHigh = (!prev || pt.y <= prev.y) && (!next || pt.y <= next.y);
          const offsetY = isLocalHigh ? -14 * vpr : 16 * vpr;
          ctx.textBaseline = isLocalHigh ? 'bottom' : 'top';

          // Label background
          const tw = ctx.measureText(label).width;
          const lx = px - tw / 2 - 4 * hpr;
          const ly = py + offsetY - (isLocalHigh ? fontSize + 2 * vpr : 0);
          ctx.fillStyle = hexToRgba(s.color, 0.15);
          ctx.beginPath();
          ctx.roundRect(lx, ly, tw + 8 * hpr, fontSize + 4 * vpr, 3 * hpr);
          ctx.fill();

          // Label text
          ctx.fillStyle = s.color;
          ctx.fillText(label, px, py + offsetY);
        }

        // Dot at each point
        ctx.beginPath();
        ctx.arc(px, py, 3 * hpr, 0, Math.PI * 2);
        ctx.fillStyle = s.color;
        ctx.fill();
      }

      // Handles
      if (s.isSelected) {
        for (const pt of pts) {
          if (pt.x != null) drawHandle(ctx, pt.x, pt.y, hpr, vpr);
        }
      }
      ctx.restore();
    })}};
  }
}

export class LabeledPathPrimitive extends Base {
  constructor(points, options = {}) {
    super();
    this.points = [...points];
    this.color = options.color || '#f59e0b';
    this.labels = options.labels || [];
    this.showFill = options.showFill || false;
    this.dashed = options.dashed || false;
    this.lineWidth = options.lineWidth || 2;
    this.labelSize = options.labelSize || 11;
    this.toolLabel = options.toolLabel || '';
    this._screenPts = [];
    this._view = new LabeledPathPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._screenPts = this.points.map(p => this._toXY(p.logical, p.price));
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    const pts = this._screenPts;
    for (let i = 1; i < pts.length; i++) {
      const p1 = pts[i-1], p2 = pts[i];
      if (p1.x == null || p2.x == null) continue;
      const dx = p2.x - p1.x, dy = p2.y - p1.y, len2 = dx*dx + dy*dy;
      if (len2 === 0) continue;
      const t = Math.max(0, Math.min(1, ((x - p1.x)*dx + (y - p1.y)*dy) / len2));
      const d = Math.hypot(x - (p1.x + t*dx), y - (p1.y + t*dy));
      if (d <= HIT_TOLERANCE) return d;
    }
    return Infinity;
  }
  hitTestHandle(x, y) {
    for (let i = 0; i < this._screenPts.length; i++) {
      const p = this._screenPts[i];
      if (p && Math.hypot(x - p.x, y - p.y) < HANDLE_HIT) return i;
    }
    return -1;
  }
  moveBy(dL, dP) {
    this.points = this.points.map(p => ({ logical: p.logical + dL, price: p.price + dP }));
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i >= 0 && i < this.points.length) {
      this.points[i] = { logical: l, price: p };
      this._requestUpdate?.();
    }
  }
  setPreviewPoint(pt) {
    if (this.points.length > 0) {
      this.points[this.points.length - 1] = pt;
      this._requestUpdate?.();
    }
  }
  addPoint(pt) { this.points.push(pt); this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// CYCLE — Circle defined by center + edge point (2 clicks)
// ═══════════════════════════════════════════════════════════════════

class CyclePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const c = s._center, e = s._edge;
      if (!c || !e || c.x == null || e.x == null) return;
      const cx = c.x * hpr, cy = c.y * vpr;
      const rx = Math.abs(e.x - c.x) * hpr;
      const ry = Math.abs(e.y - c.y) * vpr;
      const r = Math.max(rx, ry);

      ctx.save();

      // Fill
      ctx.fillStyle = hexToRgba(s.color, 0.04);
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();

      // Hover glow
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.2);
        ctx.lineWidth = 6 * hpr;
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
      }

      // Circle
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1.5 * hpr;
      ctx.setLineDash(s.dashed ? [5 * hpr, 4 * hpr] : []);
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
      ctx.setLineDash([]);

      // Center dot
      ctx.beginPath(); ctx.arc(cx, cy, 3 * hpr, 0, Math.PI * 2);
      ctx.fillStyle = s.color; ctx.fill();

      // Crosshair at center
      ctx.strokeStyle = hexToRgba(s.color, 0.3);
      ctx.lineWidth = 1 * hpr;
      ctx.beginPath();
      ctx.moveTo(cx - 8 * hpr, cy); ctx.lineTo(cx + 8 * hpr, cy);
      ctx.moveTo(cx, cy - 8 * vpr); ctx.lineTo(cx, cy + 8 * vpr);
      ctx.stroke();

      // Handles
      if (s.isSelected) {
        drawHandle(ctx, c.x, c.y, hpr, vpr);
        drawHandle(ctx, e.x, e.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class CyclePrimitive extends Base {
  constructor(centerPt, edgePt, options = {}) {
    super();
    this.centerPt = centerPt;
    this.edgePt = edgePt;
    this.color = options.color || '#818cf8';
    this.dashed = options.dashed || false;
    this._center = null; this._edge = null;
    this._view = new CyclePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._center = this._toXY(this.centerPt.logical, this.centerPt.price);
    this._edge = this._toXY(this.edgePt.logical, this.edgePt.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._center || !this._edge) return Infinity;
    const rx = Math.abs(this._edge.x - this._center.x);
    const ry = Math.abs(this._edge.y - this._center.y);
    const r = Math.max(rx, ry);
    const dist = Math.hypot(x - this._center.x, y - this._center.y);
    return Math.abs(dist - r) <= HIT_TOLERANCE ? Math.abs(dist - r) : (dist < r ? 0 : Infinity);
  }
  hitTestHandle(x, y) {
    if (this._center && Math.hypot(x - this._center.x, y - this._center.y) < HANDLE_HIT) return 0;
    if (this._edge && Math.hypot(x - this._edge.x, y - this._edge.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.centerPt = { logical: this.centerPt.logical + dL, price: this.centerPt.price + dP };
    this.edgePt = { logical: this.edgePt.logical + dL, price: this.edgePt.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i === 0) this.centerPt = { logical: l, price: p };
    else this.edgePt = { logical: l, price: p };
    this._requestUpdate?.();
  }
  setPreviewPoint(pt) { this.edgePt = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// CYCLE LINES — Repeating vertical lines at equal time intervals
// ═══════════════════════════════════════════════════════════════════

class CycleLinesPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      const interval = Math.abs(p2.x - p1.x) * hpr;
      if (interval < 2) return;

      ctx.save();
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1 * hpr;
      ctx.setLineDash([4 * hpr, 4 * hpr]);

      const startX = Math.min(p1.x, p2.x) * hpr;
      // Draw lines extending to the right
      for (let x = startX; x < bs.width + interval; x += interval) {
        if (s.isHovered || s.isSelected) {
          ctx.save();
          ctx.strokeStyle = hexToRgba(s.color, 0.15);
          ctx.lineWidth = 4 * hpr;
          ctx.setLineDash([]);
          ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, bs.height); ctx.stroke();
          ctx.restore();
          ctx.strokeStyle = s.color;
          ctx.lineWidth = 1 * hpr;
          ctx.setLineDash([4 * hpr, 4 * hpr]);
        }
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, bs.height); ctx.stroke();
      }
      // Also draw to the left
      for (let x = startX - interval; x > -interval; x -= interval) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, bs.height); ctx.stroke();
      }

      ctx.setLineDash([]);

      // Shaded bands (alternating)
      let band = 0;
      for (let x = startX - interval * 10; x < bs.width + interval; x += interval) {
        if (band % 2 === 0) {
          ctx.fillStyle = hexToRgba(s.color, 0.03);
          ctx.fillRect(x, 0, interval, bs.height);
        }
        band++;
      }

      if (s.isSelected) {
        drawHandle(ctx, p1.x, bs.height / vpr / 2, hpr, vpr);
        drawHandle(ctx, p2.x, bs.height / vpr / 2, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class CycleLinesPrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super();
    this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#818cf8';
    this._p1 = null; this._p2 = null;
    this._view = new CycleLinesPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const interval = Math.abs(this._p2.x - this._p1.x);
    if (interval < 1) return Infinity;
    const startX = Math.min(this._p1.x, this._p2.x);
    const offset = ((x - startX) % interval + interval) % interval;
    return Math.min(offset, interval - offset) <= HIT_TOLERANCE ? 0 : Infinity;
  }
  hitTestHandle(x, y) {
    const midY = this._p1 ? (this._p1.y + this._p2.y) / 2 : 0;
    if (this._p1 && Math.hypot(x - this._p1.x, y - midY) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - midY) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i === 0) this.pt1 = { logical: l, price: p };
    else this.pt2 = { logical: l, price: p };
    this._requestUpdate?.();
  }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// SINE WAVE — Sine curve between two points
// ═══════════════════════════════════════════════════════════════════

class SineWavePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      const x1 = p1.x * hpr, y1 = p1.y * vpr;
      const x2 = p2.x * hpr, y2 = p2.y * vpr;
      const amplitude = Math.abs(y2 - y1) / 2;
      const midY = (y1 + y2) / 2;
      const wavelength = Math.abs(x2 - x1);
      if (wavelength < 2) return;

      ctx.save();

      // Glow
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.2);
        ctx.lineWidth = 6 * hpr;
        ctx.beginPath();
        for (let x = 0; x <= bs.width; x += 2) {
          const phase = ((x - x1) / wavelength) * Math.PI * 2;
          const y = midY + amplitude * Math.sin(phase);
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      // Sine curve extending across chart
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1.5 * hpr;
      ctx.beginPath();
      for (let x = 0; x <= bs.width; x += 2) {
        const phase = ((x - x1) / wavelength) * Math.PI * 2;
        const y = midY + amplitude * Math.sin(phase);
        if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();

      // Center line
      ctx.strokeStyle = hexToRgba(s.color, 0.2);
      ctx.lineWidth = 1 * hpr;
      ctx.setLineDash([3 * hpr, 3 * hpr]);
      ctx.beginPath(); ctx.moveTo(0, midY); ctx.lineTo(bs.width, midY); ctx.stroke();
      ctx.setLineDash([]);

      if (s.isSelected) {
        drawHandle(ctx, p1.x, p1.y, hpr, vpr);
        drawHandle(ctx, p2.x, p2.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class SineWavePrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super();
    this.pt1 = pt1; this.pt2 = pt2;
    this.color = options.color || '#818cf8';
    this._p1 = null; this._p2 = null;
    this._view = new SineWavePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const wavelength = Math.abs(this._p2.x - this._p1.x);
    if (wavelength < 1) return Infinity;
    const amplitude = Math.abs(this._p2.y - this._p1.y) / 2;
    const midY = (this._p1.y + this._p2.y) / 2;
    const phase = ((x - this._p1.x) / wavelength) * Math.PI * 2;
    const expectedY = midY + amplitude * Math.sin(phase);
    return Math.abs(y - expectedY) <= HIT_TOLERANCE ? Math.abs(y - expectedY) : Infinity;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i === 0) this.pt1 = { logical: l, price: p };
    else this.pt2 = { logical: l, price: p };
    this._requestUpdate?.();
  }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}
